import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useBranch } from '../contexts/BranchContext';

interface LeadSource {
  id: number;
  name: string;
  description: string;
  orderIndex: number;
  isDefault: boolean;
  isActive: boolean;
  leadsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface LeadSourcesManagerProps {
  onClose: () => void;
  onSourceUpdate?: () => void;
}

const LeadSourcesManager: React.FC<LeadSourcesManagerProps> = ({ onClose, onSourceUpdate }) => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [sources, setSources] = useState<LeadSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingSource, setEditingSource] = useState<LeadSource | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedSource, setDraggedSource] = useState<LeadSource | null>(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Load sources
  const loadSources = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leads/sources?includeInactive=true');
      if (response.data.success) {
        setSources(response.data.data);
      } else {
        setError('Failed to load sources');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading sources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchLoading) {
      loadSources();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading]);

  // Handle form changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setEditingSource(null);
    setShowAddForm(false);
  };

  // Initialize default sources
  const initializeDefaults = async () => {
    if (!currentBranch?.id) {
      setError('Please select a branch first');
      return;
    }

    if (!window.confirm('This will create default lead sources for the current branch. Continue?')) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/leads/sources/initialize-defaults', {
        branchId: currentBranch.id
      });
      
      if (response.data.success) {
        await loadSources();
        setError(null);
        alert(`Successfully created ${response.data.data.length} default lead sources!`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error initializing default sources');
    } finally {
      setSaving(false);
    }
  };

  // Start editing a source
  const startEdit = (source: LeadSource) => {
    setFormData({
      name: source.name,
      description: source.description || ''
    });
    setEditingSource(source);
    setShowAddForm(false);
  };

  // Save source (create or update)
  const saveSource = async () => {
    if (!formData.name.trim()) {
      setError('Source name is required');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (editingSource) {
        // Update existing source
        response = await api.put(`/leads/sources/${editingSource.id}`, formData);
      } else {
        // Create new source
        response = await api.post('/leads/sources', formData);
      }

      if (response.data.success) {
        await loadSources();
        resetForm();
        onSourceUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to save source');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving source');
    } finally {
      setSaving(false);
    }
  };

  // Delete source
  const deleteSource = async (source: LeadSource) => {
    if (source.leadsCount > 0) {
      setError(`Cannot delete "${source.name}" because it has ${source.leadsCount} leads assigned to it`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the source "${source.name}"?`)) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.delete(`/leads/sources/${source.id}`);
      if (response.data.success) {
        await loadSources();
        onSourceUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to delete source');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting source');
    } finally {
      setSaving(false);
    }
  };

  // Set default source
  const setDefaultSource = async (source: LeadSource) => {
    setSaving(true);
    try {
      const response = await api.post(`/leads/sources/${source.id}/set-default`);
      if (response.data.success) {
        await loadSources();
        onSourceUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to set default source');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error setting default source');
    } finally {
      setSaving(false);
    }
  };

  // Toggle source active status
  const toggleSourceActive = async (source: LeadSource) => {
    if (!source.isActive && source.leadsCount > 0) {
      setError(`Cannot deactivate "${source.name}" because it has ${source.leadsCount} leads assigned to it`);
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/leads/sources/${source.id}`, {
        ...source,
        isActive: !source.isActive
      });
      if (response.data.success) {
        await loadSources();
        onSourceUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to update source');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating source');
    } finally {
      setSaving(false);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, source: LeadSource) => {
    setDraggedSource(source);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, targetSource: LeadSource) => {
    e.preventDefault();
    
    if (!draggedSource || draggedSource.id === targetSource.id) {
      setDraggedSource(null);
      return;
    }

    // Create new order based on drop position
    const activeSources = sources.filter(s => s.isActive).sort((a, b) => a.orderIndex - b.orderIndex);
    const targetIndex = activeSources.findIndex(s => s.id === targetSource.id);
    const draggedIndex = activeSources.findIndex(s => s.id === draggedSource.id);

    // Remove the dragged source from its current position
    const reorderedSources = activeSources.filter(s => s.id !== draggedSource.id);
    
    // Insert the dragged source at the target position
    reorderedSources.splice(targetIndex > draggedIndex ? targetIndex - 1 : targetIndex, 0, draggedSource);

    // Update order indexes
    const sourceOrders = reorderedSources.map((source, index) => ({
      id: source.id,
      orderIndex: index + 1
    }));

    setSaving(true);
    try {
      const response = await api.post('/leads/sources/reorder', { sourceOrders });
      if (response.data.success) {
        await loadSources();
        onSourceUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to reorder sources');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error reordering sources');
    } finally {
      setSaving(false);
      setDraggedSource(null);
    }
  };

  // Get source icon based on name
  const getSourceIcon = (sourceName: string) => {
    const name = sourceName.toLowerCase();
    if (name.includes('website') || name.includes('web')) {
      return (
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
        </svg>
      );
    } else if (name.includes('social') || name.includes('facebook') || name.includes('twitter') || name.includes('linkedin')) {
      return (
        <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    } else if (name.includes('email') || name.includes('campaign')) {
      return (
        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    } else if (name.includes('referral') || name.includes('partner')) {
      return (
        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      );
    } else if (name.includes('ad') || name.includes('advertising') || name.includes('google') || name.includes('facebook ads')) {
      return (
        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      );
    } else if (name.includes('cold') || name.includes('outreach') || name.includes('call')) {
      return (
        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      );
    } else if (name.includes('event') || name.includes('trade') || name.includes('show') || name.includes('conference')) {
      return (
        <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    
    // Default icon
    return (
      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading sources...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage Lead Sources</h2>
            <p className="text-sm text-gray-600 mt-1">Track and organize where your leads come from</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error Alert */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sources List */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Current Sources</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={initializeDefaults}
                    disabled={saving || !currentBranch?.id}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Initialize Defaults
                  </button>
                  <button
                    onClick={() => {
                      resetForm();
                      setShowAddForm(true);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    Add Source
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {sources
                  .filter(source => source.isActive)
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((source) => (
                    <div
                      key={source.id}
                      draggable={!saving}
                      onDragStart={(e) => handleDragStart(e, source)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, source)}
                      className={`p-3 border rounded-lg transition-all ${
                        draggedSource?.id === source.id 
                          ? 'opacity-50 scale-95' 
                          : 'hover:shadow-md cursor-move'
                      } ${saving ? 'pointer-events-none' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            {/* Drag handle */}
                            <div className="cursor-move text-gray-400 hover:text-gray-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                              </svg>
                            </div>
                            {getSourceIcon(source.name)}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{source.name}</span>
                              {source.isDefault && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                            {source.description && (
                              <p className="text-sm text-gray-600">{source.description}</p>
                            )}
                            <p className="text-xs text-gray-500">{source.leadsCount} leads</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {!source.isDefault && (
                            <button
                              onClick={() => setDefaultSource(source)}
                              disabled={saving}
                              className="text-xs text-gray-500 hover:text-green-600 px-2 py-1 rounded"
                              title="Set as default"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => startEdit(source)}
                            disabled={saving}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded"
                            title="Edit source"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => deleteSource(source)}
                            disabled={saving || source.leadsCount > 0}
                            className="text-red-600 hover:text-red-800 p-1 rounded disabled:text-gray-400 disabled:cursor-not-allowed"
                            title={source.leadsCount > 0 ? 'Cannot delete source with leads' : 'Delete source'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Inactive Sources */}
              {sources.some(source => !source.isActive) && (
                <div className="mt-6">
                  <h4 className="text-md font-medium text-gray-700 mb-2">Inactive Sources</h4>
                  <div className="space-y-2">
                    {sources
                      .filter(source => !source.isActive)
                      .map((source) => (
                        <div key={source.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {getSourceIcon(source.name)}
                              <div>
                                <span className="font-medium text-gray-700">{source.name}</span>
                                <p className="text-xs text-gray-500">{source.leadsCount} leads</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => toggleSourceActive(source)}
                                disabled={saving}
                                className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded"
                              >
                                Activate
                              </button>
                              <button
                                onClick={() => deleteSource(source)}
                                disabled={saving || source.leadsCount > 0}
                                className="text-red-600 hover:text-red-800 p-1 rounded disabled:text-gray-400"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add/Edit Form */}
            {(showAddForm || editingSource) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingSource ? 'Edit Source' : 'Add New Source'}
                </h3>
                
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  {/* Source Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter source name"
                      maxLength={100}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description of this lead source"
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={resetForm}
                      disabled={saving}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveSource}
                      disabled={saving || !formData.name.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {saving && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                      {saving ? 'Saving...' : (editingSource ? 'Update Source' : 'Add Source')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Source Performance Stats */}
            {!showAddForm && !editingSource && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Source Performance</h3>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="space-y-3">
                    {sources
                      .filter(source => source.isActive && source.leadsCount > 0)
                      .sort((a, b) => b.leadsCount - a.leadsCount)
                      .slice(0, 5)
                      .map((source) => (
                        <div key={source.id} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {getSourceIcon(source.name)}
                            <span className="text-sm font-medium text-gray-700">{source.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{source.leadsCount} leads</span>
                            <div className="w-16 h-2 bg-gray-200 rounded-full">
                              <div 
                                className="h-2 bg-blue-500 rounded-full"
                                style={{ 
                                  width: `${Math.min(100, (source.leadsCount / Math.max(...sources.map(s => s.leadsCount))) * 100)}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    
                    {sources.filter(source => source.isActive && source.leadsCount > 0).length === 0 && (
                      <div className="text-center py-6">
                        <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p className="text-sm text-gray-500 mt-2">No data available yet</p>
                        <p className="text-xs text-gray-400">Start adding leads to see source performance</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Tips</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• The default source is automatically assigned to new leads</li>
              <li>• Sources with leads cannot be deleted - deactivate them instead</li>
              <li>• Use descriptive names to better track lead attribution</li>
              <li>• Drag and drop sources to reorder them</li>
              <li>• Monitor source performance to optimize your lead generation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadSourcesManager;
